import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LayoutGrid, Theater, UtensilsCrossed, FileSpreadsheet } from "lucide-react";
import ProjectBudget from "./ProjectBudget";
import SimpleBudget from "./SimpleBudget";
import BudgetExcelImportDialog from "@/components/BudgetExcelImportDialog";

const TEMPLATES = [
  { key: "standard", title: "Estándar", desc: "Presupuestador completo: capítulos, recursos internos/externos, tarifas y márgenes.", icon: LayoutGrid, testid: "tpl-standard" },
  { key: "onstage", title: "OnStage", desc: "Hoja simplificada para espectáculos/shows. Precargada con sus secciones y partidas, editable.", icon: Theater, testid: "tpl-onstage" },
  { key: "dining", title: "Dining", desc: "Hoja simplificada para restaurantes/menús. Precargada con sus secciones y partidas, editable.", icon: UtensilsCrossed, testid: "tpl-dining" },
];

const Spinner = () => <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

export default function BudgetTab({ projectId, mode }) {
  const [kind, setKind] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/projects/${projectId}/budget-kind`).then((r) => { if (alive) setKind(r.data.kind); }).catch(() => { if (alive) setKind("none"); });
    return () => { alive = false; };
  }, [projectId]);

  if (kind === null) return <Spinner />;
  if (kind === "simple") return <SimpleBudget projectId={projectId} mode={mode} onReset={() => setKind("none")} />;
  if (kind === "standard") return <ProjectBudget projectId={projectId} mode={mode} onReset={() => setKind("none")} />;

  const canEdit = mode === "manage" || mode === "edit";
  if (!canEdit) return <p className="text-sm text-muted-foreground text-center py-10">Este proyecto todavía no tiene presupuesto.</p>;

  const pick = async (key) => {
    if (key === "standard") { setKind("standard"); return; }
    setBusy(true);
    try { await api.post(`/projects/${projectId}/simple-budget/init`, { template: key }); setKind("simple"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="budget-template-chooser">
      <div>
        <h3 className="font-heading font-bold text-lg">Elige una plantilla de presupuesto</h3>
        <p className="text-sm text-muted-foreground">Podrás editarlo después. OnStage y Dining vienen precargadas; en el cierre puedes ver los costes sin el margen (excepto restaurantes/shows).</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {TEMPLATES.map((t) => (
          <Card key={t.key} className="p-5 flex flex-col gap-3 border-border hover:border-primary/50 transition-colors">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><t.icon className="h-5 w-5" /></div>
            <div className="flex-1">
              <div className="font-heading font-bold">{t.title}</div>
              <p className="text-sm text-muted-foreground mt-1">{t.desc}</p>
            </div>
            <Button onClick={() => pick(t.key)} disabled={busy} data-testid={t.testid}>Usar {t.title}</Button>
          </Card>
        ))}
        <Card className="p-5 flex flex-col gap-3 border-border border-dashed hover:border-primary/50 transition-colors">
          <div className="h-10 w-10 rounded-lg bg-gold/15 text-[#a9741c] flex items-center justify-center"><FileSpreadsheet className="h-5 w-5" /></div>
          <div className="flex-1">
            <div className="font-heading font-bold">Importar Excel</div>
            <p className="text-sm text-muted-foreground mt-1">Carga un presupuesto desde un Excel (Concepto / Precio / Und / Total). Luego podrás marcar cada partida como horas internas o gasto externo.</p>
          </div>
          <Button variant="outline" onClick={() => setImportOpen(true)} disabled={busy} data-testid="tpl-import-excel">Importar Excel</Button>
        </Card>
      </div>
      <BudgetExcelImportDialog projectId={projectId} open={importOpen} onOpenChange={setImportOpen} onImported={() => setKind("simple")} />
    </div>
  );
}
